import axios from 'axios';
import { API_URL } from '../config';
import authService from './authService';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Basic ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      authService.logout();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default apiClient;


export async function getSiteVisitForms(companyId, userId = null) {
  if (!companyId) throw new Error('companyId required');
  
  // If userId is provided, use user-specific endpoint
  let url;
  if (userId) {
    url = `${API_URL}/companies/${companyId}/site_visit_forms/users/${userId}`;
  } else {
    url = `${API_URL}/companies/${companyId}/site_visit_forms/list`;
  }
  
  const resp = await apiClient.get(url);
  return resp.data;
}

export async function getSiteVisitFormsById(companyId, formId) {
  if (!companyId) throw new Error('companyId required');
  if (!formId) throw new Error('formId required');
  const url = `${API_URL}/companies/${companyId}/site_visit_forms/${formId}`;
  const resp = await apiClient.get(url);
  return resp.data.data;
}


export async function getLeadByPhoneNumber(companyId,phone) {
  if (!companyId) throw new Error('companyId required');
  if (!phone) throw new Error('phone required');
  const url = `${API_URL}/companies/${companyId}/leads/search?q=${phone}`;
  const resp = await apiClient.get(url);
  return resp;
}

export async function getLeadById(companyId, leadId) {
  if (!companyId) throw new Error('companyId required');
  if (!leadId) throw new Error('leadId required');
  const url = `${API_URL}/companies/${companyId}/leads/${leadId}`;
  const resp = await apiClient.get(url);
  return resp.data;
}

export async function upsertLead(companyId, leadData) {
  if (!companyId) throw new Error('companyId required');
  if (!leadData) throw new Error('leadData required');
  
  const url = `${API_URL}/companies/${companyId}/leads/upsert`;
  
  // Filter out empty values
  const filteredData = Object.fromEntries(
    Object.entries(leadData).filter(([_, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  );
  console.log("API Servive",filteredData);
  
  const resp = await apiClient.post(url, filteredData);
  return resp.data;
}

export async function assignLead(companyId, leadId, toUserEmail) {
  if (!companyId) throw new Error('companyId required');
  if (!leadId) throw new Error('leadId required');
  if (!toUserEmail) throw new Error('toUserEmail required');
  
  const url = `${API_URL}/companies/${companyId}/leads/${leadId}/assign`;
  
  const resp = await apiClient.post(url, {

      to_user_email: toUserEmail

  });
  
  return resp.data;
}

export async function getKernelParams(companyId) {
  if (!companyId) throw new Error('companyId required');
  const url = `${API_URL}/companies/${companyId}/leads/kernel_params_json`;
  const resp = await apiClient.get(url);
  return resp.data;
}

// Cache for kernel params to avoid repeated API calls
let kernelParamsCache = null;
let kernelParamsCacheCompanyId = null;

export async function getFieldChoices(companyId, fieldName) {
  if (!companyId) throw new Error('companyId required');
  if (!fieldName) throw new Error('fieldName required');

  // Fetch kernel params if not cached or company changed
  if (!kernelParamsCache || kernelParamsCacheCompanyId !== companyId) {
    const data = await getKernelParams(companyId);
    kernelParamsCache = data;
    kernelParamsCacheCompanyId = companyId;
  }

  // Find the field by name
  const field = kernelParamsCache.lead_fields?.find(
    f => f.name === fieldName || f.label === fieldName
  );

  if (!field) {
    console.warn(`Field "${fieldName}" not found in kernel params`);
    return [];
  }

  // Return choices array
  return field.choices || [];
}

export async function getFieldChoicesByMultipleNames(companyId, fieldNames) {
  if (!companyId) throw new Error('companyId required');
  if (!fieldNames || !Array.isArray(fieldNames)) {
    throw new Error('fieldNames must be an array');
  }

  // Fetch kernel params if not cached or company changed
  if (!kernelParamsCache || kernelParamsCacheCompanyId !== companyId) {
    const data = await getKernelParams(companyId);
    kernelParamsCache = data;
    kernelParamsCacheCompanyId = companyId;
  }

  const result = {};

  fieldNames.forEach(fieldName => {
    const field = kernelParamsCache.lead_fields?.find(
      f => f.name === fieldName || f.label === fieldName
    );

    if (field) {
      result[fieldName] = field.choices || [];
    } else {
      console.warn(`Field "${fieldName}" not found in kernel params`);
      result[fieldName] = [];
    }
  });

  return result;
}

// Clear cache function (useful when data needs to be refreshed)
export function clearKernelParamsCache() {
  kernelParamsCache = null;
  kernelParamsCacheCompanyId = null;
}
